/**
 * The library as folders, not just as one flat list.
 *
 * A curator thinks in folders — "the 2016 imports", "the jingles" — and a flat
 * grid of 100k rows offers no way to act on one of those groups. This is where a
 * folder becomes something you can point at: click to filter the grid to it,
 * rescan it, or retire the whole thing.
 *
 * Counts are shown per node and rolled up from descendants, so a collapsed root
 * still tells you how much is under it and how much of that is missing or
 * archived. Missing and archived are counted separately because they are
 * different events — a file that vanished is not a track someone retired.
 */

import { useEffect, useState, type DragEvent } from "react";
import { treeFolder, type TreeNode } from "./api";

function Counts({ node }: { node: TreeNode }) {
  const { tracks, missing, archived } = node.counts;
  return (
    <span className="tree-counts">
      {tracks > 0 && <span className="tree-count">{tracks}</span>}
      {missing > 0 && (
        <span className="tree-count missing" title={`${missing} file(s) no longer on disk`}>
          {missing}
        </span>
      )}
      {archived > 0 && (
        <span className="tree-count archived" title={`${archived} archived`}>
          {archived}
        </span>
      )}
    </span>
  );
}

function Node({
  node,
  depth,
  activePath,
  onPick,
  onAction,
  onCreate,
  onDropFiles,
}: {
  node: TreeNode;
  depth: number;
  activePath: string | null;
  onPick: (node: TreeNode) => void;
  onAction: (node: TreeNode, action: "rescan" | "archive" | "remove" | "restore" | "delete" | "manual") => void;
} & Extras) {
  // Roots start open; deeper folders start closed, so a big library does not
  // arrive as a wall of directories.
  const [open, setOpen] = useState(depth === 0);
  const [menu, setMenu] = useState(false);
  // Naming a new folder inside this one; and whether songs are being dragged over it.
  const [naming, setNaming] = useState<string | null>(null);
  const [dropping, setDropping] = useState(false);
  const accepts = !!onDropFiles && !node.removed;
  const carriesFiles = (e: DragEvent) => Array.from(e.dataTransfer.types).includes("Files");
  const create = async () => {
    const name = (naming ?? "").trim();
    if (!name || !onCreate) return setNaming(null);
    const error = await onCreate(node, name);
    if (!error) {
      setNaming(null);
      setOpen(true);
    }
  };
  // A big library's tree arrives a level at a time: a folder whose own folders were
  // left out (`more`) asks for them when it opens, and again after each refresh.
  const [loaded, setLoaded] = useState<{ of: TreeNode; children: TreeNode[]; total: number } | null>(null);
  const mine = loaded?.of === node ? loaded : null;
  const children = mine?.children ?? node.children;
  const total = mine?.total ?? node.childTotal ?? children.length;
  useEffect(() => {
    if (!open || !node.more || mine) return;
    let live = true;
    void treeFolder(node.rootId ?? node.id ?? 0, node.kind === "root" ? "" : node.path ?? "").then((got) => {
      if (live && got) setLoaded({ of: node, children: got.children, total: got.childTotal ?? got.children.length });
    });
    return () => {
      live = false;
    };
  }, [open, node, mine]);
  const showMore = async () => {
    const got = await treeFolder(node.rootId ?? node.id ?? 0, node.kind === "root" ? "" : node.path ?? "", children.length);
    if (got) setLoaded({ of: node, children: [...children, ...got.children], total: got.childTotal ?? total });
  };
  const hasChildren = node.children.length > 0 || !!node.more;
  const id = `${node.rootId ?? node.id}:${node.path ?? ""}`;
  const isRoot = node.kind === "root";

  return (
    <div className="tree-node">
      <div
        className={`tree-row ${activePath === id ? "active" : ""} ${
          node.removed ? "removed" : ""
        } ${dropping ? "drop-files" : ""}`}
        style={{ paddingLeft: 6 + depth * 12 }}
        title={accepts ? "Drop songs here to copy them into this folder" : undefined}
        // Right-click opens the same menu as the ⋯ button (Hand-picked only, Rescan, ...).
        onContextMenu={(e) => {
          e.preventDefault();
          setMenu(true);
        }}
        onDragOver={(e) => {
          if (!accepts || !carriesFiles(e)) return;
          e.preventDefault();
          e.dataTransfer.dropEffect = "copy";
          if (!dropping) setDropping(true);
        }}
        onDragLeave={() => setDropping(false)}
        onDrop={(e) => {
          if (!accepts || !carriesFiles(e)) return;
          e.preventDefault();
          setDropping(false);
          onDropFiles?.(node, Array.from(e.dataTransfer.files));
        }}
      >
        <button
          className="tree-twisty"
          onClick={() => setOpen((o) => !o)}
          style={{ visibility: hasChildren ? "visible" : "hidden" }}
          aria-label={open ? "Collapse" : "Expand"}
        >
          {open ? "▾" : "▸"}
        </button>

        <button className="tree-label" onClick={() => onPick(node)} title={node.path || node.name}>
          <span className="tree-icon">{isRoot ? "▣" : "▸"}</span>
          {node.name}
          {node.removed && <em> · removed</em>}
          {(node.manualOnly || node.manualInherited) && (
            <span
              className={`tree-manual ${node.manualOnly ? "" : "inherited"}`}
              title={node.manualOnly
                ? "Hand-picked only: Auto DJ and Flow never pick these songs; search and add them yourself. Clip rules still use clips in here."
                : "Hand-picked only, because a folder above it is"}
            >
              hand-picked
            </span>
          )}
        </button>

        <Counts node={node} />

        <button
          className="tree-menu-button"
          onClick={() => setMenu((m) => !m)}
          title="Folder actions"
        >
          ⋯
        </button>

        {menu && (
          <>
            <div className="scrim" onClick={() => setMenu(false)} />
            <div className="tree-menu">
              {onCreate && !node.removed && (
                <button
                  onClick={() => {
                    setMenu(false);
                    setNaming("");
                  }}
                >
                  New folder here…
                </button>
              )}
              <button
                onClick={() => {
                  setMenu(false);
                  onAction(node, "rescan");
                }}
              >
                Rescan this folder
              </button>
              <button
                onClick={() => {
                  setMenu(false);
                  onAction(node, "archive");
                }}
              >
                Archive everything in it
              </button>
              {isRoot && (
                <>
                  <div className="menu-sep" />
                  {node.removed ? (
                    <button
                      onClick={() => {
                        setMenu(false);
                        onAction(node, "restore");
                      }}
                    >
                      Restore this library folder
                    </button>
                  ) : (
                    <button
                      className="danger"
                      onClick={() => {
                        setMenu(false);
                        onAction(node, "remove");
                      }}
                    >
                      Remove from library
                    </button>
                  )}
                </>
              )}
              {!isRoot && (
                <>
                  <button
                    disabled={node.manualInherited && !node.manualOnly}
                    title={node.manualInherited && !node.manualOnly
                      ? "A folder above this one is hand-picked only; change it there"
                      : "Auto DJ and Flow never pick songs from this folder (or the folders in it). Search and add them yourself; clip rules still use clips in it."}
                    onClick={() => {
                      setMenu(false);
                      onAction(node, "manual");
                    }}
                  >
                    {node.manualOnly || node.manualInherited ? "✓ " : ""}Hand-picked only
                  </button>
                  <div className="menu-sep" />
                  <button
                    className="danger"
                    title="Deletes the folder from disk. Only an empty folder: move or delete its songs first."
                    onClick={() => {
                      setMenu(false);
                      onAction(node, "delete");
                    }}
                  >
                    Delete folder
                  </button>
                </>
              )}
            </div>
          </>
        )}
      </div>

      {naming !== null && (
        <div className="tree-row tree-new" style={{ paddingLeft: 18 + (depth + 1) * 12 }}>
          <input
            className="nv-input tree-new-input"
            autoFocus
            placeholder="New folder name"
            aria-label={`New folder inside ${node.name}`}
            value={naming}
            onChange={(e) => setNaming(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter") void create();
              if (e.key === "Escape") setNaming(null);
            }}
            onBlur={() => void create()}
          />
        </div>
      )}

      {open &&
        children.map((child) => (
          <Node
            key={`${child.rootId}:${child.path}`}
            node={child}
            depth={depth + 1}
            activePath={activePath}
            onPick={onPick}
            onAction={onAction}
            onCreate={onCreate}
            onDropFiles={onDropFiles}
          />
        ))}
      {open && total > children.length && (
        <button className="tree-row tree-more" style={{ paddingLeft: 18 + (depth + 1) * 12 }} onClick={() => void showMore()}>
          Show more ({(total - children.length).toLocaleString()} folders)
        </button>
      )}
    </div>
  );
}

interface Extras {
  /** Make a folder `name` inside `node`. Returns an error to show, or null. */
  onCreate?: (node: TreeNode, name: string) => Promise<string | null>;
  /** Songs dropped on `node` from the computer: copy them into it. */
  onDropFiles?: (node: TreeNode, files: File[]) => void;
}

export function LibraryTree({
  roots,
  activePath,
  onPick,
  onAction,
  onCreate,
  onDropFiles,
}: {
  roots: TreeNode[];
  activePath: string | null;
  onPick: (node: TreeNode) => void;
  onAction: (node: TreeNode, action: "rescan" | "archive" | "remove" | "restore" | "delete" | "manual") => void;
} & Extras) {
  if (roots.length === 0) {
    return <div className="tree-empty">No folders added yet.</div>;
  }

  return (
    <div className="tree">
      {roots.map((root) => (
        <Node
          key={root.id}
          node={root}
          depth={0}
          activePath={activePath}
          onPick={onPick}
          onAction={onAction}
          onCreate={onCreate}
          onDropFiles={onDropFiles}
        />
      ))}
    </div>
  );
}
